import { useState, useEffect } from "react"
import { Elements } from '@stripe/react-stripe-js'
import { getStripePromise, elementsOptions } from '@/lib/stripe'
import { Button } from "@/components/ui/button"
import type { Stripe } from '@stripe/stripe-js'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { CreditCard, AlertCircle, CheckCircle2, RefreshCw } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useCurrency } from "@/hooks/useCurrency"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { autoTopupQueries } from "@/services/autoTopupApi"
import { accountQueries } from "@/services/accountApi"
import { toast } from "sonner"
import { trackEvent } from "@/lib/analytics"
import StripePaymentForm from "./StripePaymentForm"

interface StripeTopUpModalProps {
  isOpen: boolean
  onClose: () => void
  stripePublishableKey: string
  /** Called after a successful payment (e.g. to invalidate extra queries). Optional. */
  onTopUp?: () => void
}

export default function StripeTopUpModal({ isOpen, onClose, stripePublishableKey, onTopUp }: StripeTopUpModalProps) {
  const { t } = useTranslation(['app', 'common'])
  const { currency, formatCurrency } = useCurrency()
  const queryClient = useQueryClient()

  const topupConfig = currency.topupConfig
  const presetAmounts = topupConfig.presets
  const minAmount = topupConfig.min
  const maxAmount = topupConfig.max

  const [amount, setAmount] = useState<string>("")
  const [error, setError] = useState<string>("")
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [successAmount, setSuccessAmount] = useState(0)
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null)
  const [enableAutoTopup, setEnableAutoTopup] = useState(false)
  const [autoTopupThreshold, setAutoTopupThreshold] = useState<number>(topupConfig.defaultThreshold)
  const [autoTopupAmount, setAutoTopupAmount] = useState<number>(topupConfig.defaultAmount)

  const { data: balanceData } = useQuery(accountQueries.balance())
  const currentBalance = (balanceData?.accountBalance?.amount ?? 0) / 100

  const { data: autoTopupConfig } = useQuery(autoTopupQueries.config())
  const hasAutoTopupEnabled = autoTopupConfig?.enabled === true

  // Lazy load Stripe only when modal opens
  useEffect(() => {
    if (isOpen && !stripePromise && stripePublishableKey) {
      setStripePromise(getStripePromise(stripePublishableKey))
    }
  }, [isOpen, stripePromise, stripePublishableKey])

  const handlePresetAmount = (presetAmount: number) => {
    setAmount(presetAmount.toString())
    setError("")
  }

  const handleAmountChange = (value: string) => {
    const numericValue = value.replace(/[^0-9.]/g, "")
    const parts = numericValue.split(".")
    if (parts.length > 2) return
    if (parts[1] && parts[1].length > 2) return
    setAmount(numericValue)
    setError("")
    setPaymentSuccess(false)
  }

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    if (!amount || isNaN(parsedAmount) || parsedAmount < minAmount || parsedAmount > maxAmount) {
      setError(t('topUp.errors.invalidAmount'))
      return
    }
    try {
      trackEvent('top-up-completed', { revenue: parsedAmount, currency: currency.code })
      setSuccessAmount(parsedAmount)
      setAmount("")
      setPaymentSuccess(true)
      queryClient.invalidateQueries({ queryKey: accountQueries.balance().queryKey })
      onTopUp?.()
      setTimeout(() => {
        onClose()
        setPaymentSuccess(false)
      }, 2000)
    } catch {
      toast.error(t('topUp.errors.paymentFailed'))
    }
  }

  const handleStripePaymentError = (errorMessage: string) => {
    setError(errorMessage)
  }

  const parsedAmount = parseFloat(amount)
  const newBalance = currentBalance + (isNaN(parsedAmount) ? 0 : parsedAmount)
  const isAmountValid = !isNaN(parsedAmount) && parsedAmount >= minAmount && parsedAmount <= maxAmount
  const numAmount = isNaN(parsedAmount) ? 0 : parsedAmount

  const handleDialogClose = () => {
    if (!isProcessingPayment) onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t('topUp.title')}
          </DialogTitle>
          <DialogDescription>
            {t('topUp.description')}
          </DialogDescription>
        </DialogHeader>

        {paymentSuccess ? (
          <div className="text-center py-8 space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">{t('topUp.success.title')}</h3>
              <p className="text-muted-foreground">
                {t('topUp.success.description', { amount: formatCurrency(successAmount) })}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Balance summary */}
            <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-4 py-3">
              <span className="text-sm text-muted-foreground">{t('topUp.currentBalance')}</span>
              <div className="text-right">
                <span className="font-semibold tabular-nums">{formatCurrency(currentBalance)}</span>
                {amount && !isNaN(parsedAmount) && (
                  <p className="text-xs text-primary tabular-nums">
                    → {formatCurrency(newBalance)}
                  </p>
                )}
              </div>
            </div>

            {/* Preset amounts */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('topUp.quickSelect')}</Label>
              <div className="grid grid-cols-4 gap-2">
                {presetAmounts.map((presetAmount) => (
                  <Button
                    key={presetAmount}
                    variant="outline"
                    size="sm"
                    onClick={() => handlePresetAmount(presetAmount)}
                    className={amount === presetAmount.toString() ? "border-primary bg-primary/10 font-semibold" : ""}
                    disabled={isProcessingPayment}
                  >
                    {formatCurrency(presetAmount)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom amount */}
            <div className="space-y-1.5">
              <Label htmlFor="top-up-amount" className="text-sm font-medium">{t('topUp.customAmount')}</Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  {currency.symbol}
                </span>
                <Input
                  id="top-up-amount"
                  type="text"
                  placeholder={t('topUp.placeholder')}
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="pl-7"
                  disabled={isProcessingPayment}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('topUp.limits', { min: formatCurrency(minAmount), max: formatCurrency(maxAmount) })}
              </p>
            </div>

            {/* Auto top-up opt-in — only show if not already configured */}
            {!hasAutoTopupEnabled && (
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <RefreshCw className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-none">
                        {t('topUp.enableAutoTopup', 'Enable auto top-up')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('topUp.autoTopupDescription', 'Automatically top up when balance runs low')}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={enableAutoTopup}
                    onCheckedChange={setEnableAutoTopup}
                    disabled={isProcessingPayment}
                    className="shrink-0"
                  />
                </div>

                {/* Expandable config fields */}
                <div
                  className={`grid transition-all duration-200 ease-in-out ${
                    enableAutoTopup ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border/60">
                      <div className="space-y-1.5">
                        <Label htmlFor="at-threshold" className="text-xs font-medium">
                          {t('topUp.autoTopupThreshold', 'Top up when below')}
                        </Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            {currency.symbol}
                          </span>
                          <Input
                            id="at-threshold"
                            type="number"
                            value={autoTopupThreshold}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value)
                              if (!isNaN(val))
                                setAutoTopupThreshold(Math.min(Math.max(val, topupConfig.min), topupConfig.max))
                            }}
                            min={topupConfig.min}
                            max={topupConfig.max}
                            step="1"
                            className="pl-7 h-8 text-sm"
                            disabled={isProcessingPayment}
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="at-amount" className="text-xs font-medium">
                          {t('topUp.autoTopupAmount', 'Top-up amount')}
                        </Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            {currency.symbol}
                          </span>
                          <Input
                            id="at-amount"
                            type="number"
                            value={autoTopupAmount}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value)
                              if (!isNaN(val))
                                setAutoTopupAmount(Math.min(Math.max(val, topupConfig.min), topupConfig.max))
                            }}
                            min={topupConfig.min}
                            max={topupConfig.max}
                            step="1"
                            className="pl-7 h-8 text-sm"
                            disabled={isProcessingPayment}
                          />
                        </div>
                      </div>
                      <p className="col-span-2 text-xs text-muted-foreground -mt-0.5">
                        {t('topUp.autoTopupLimits', 'Range: {{min}} – {{max}}', {
                          min: formatCurrency(topupConfig.min),
                          max: formatCurrency(topupConfig.max),
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Stripe Payment Form */}
            {isAmountValid && stripePromise && (
              <Elements stripe={stripePromise} options={elementsOptions}>
                <StripePaymentForm
                  amount={numAmount}
                  enableAutoTopup={enableAutoTopup}
                  autoTopupThreshold={autoTopupThreshold}
                  autoTopupAmount={autoTopupAmount}
                  onPaymentSuccess={handleStripePaymentSuccess}
                  onPaymentError={handleStripePaymentError}
                  isProcessing={isProcessingPayment}
                  setIsProcessing={setIsProcessingPayment}
                />
              </Elements>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>
        )}

        {!paymentSuccess && (
          <DialogFooter>
            <Button variant="outline" onClick={handleDialogClose} disabled={isProcessingPayment}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
